import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { PREP_GUIDES, getPrepGuideBySlug } from "@/data/faq";
import { useLocalize } from "@/data/localize";
import { SEO } from "@/lib/seo";
import { CLINIC_PHONE } from "@/lib/mailto";
import * as Icons from "lucide-react";
import { AlertTriangle, ArrowRight, Info } from "lucide-react";

export function ResourcesIndex() {
    const { t } = useTranslation();
    const { localizeList } = useLocalize();
    const guides = localizeList(PREP_GUIDES);
    return (
        <>
            <SEO title={t("resources.seoTitle")} description={t("resources.seoDescription")} />
            <div className="container-page pt-8">
                <Breadcrumbs items={[{ label: t("resources.crumb") }]} />
            </div>
            <section className="container-page pt-6 pb-4">
                <div className="max-w-3xl">
                    <span className="chip-teal">{t("resources.chip")}</span>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                        {t("resources.title")}
                    </h1>
                    <p className="mt-4 text-[color:var(--text-muted)] leading-relaxed text-lg">
                        {t("resources.intro")}
                    </p>
                </div>
            </section>
            <section className="container-page pb-4">
                <Link
                    to="/resources/bowel-prep-guide"
                    data-testid="resource-card-bowel-prep-guide"
                    className="card-soft group grid gap-0 md:grid-cols-12 items-stretch overflow-hidden mb-6"
                    style={{ background: "linear-gradient(135deg, rgba(14,124,134,0.14) 0%, rgba(255,255,255,1) 55%)" }}
                >
                    <div className="md:col-span-5 relative bg-[color:var(--bg-subtle)]">
                        <div className="aspect-[4/3] md:aspect-auto md:h-full w-full">
                            <img
                                src="/bowel-prep/step-3-clear-liquids.png"
                                alt=""
                                className="h-full w-full object-cover"
                                loading="lazy"
                                onError={(e) => (e.currentTarget.style.display = "none")}
                            />
                        </div>
                        <span className="absolute top-3 left-3 rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--brand-teal-2)] border border-[color:var(--brand-teal-2)]/25">
                            {t("resources.featuredBadge")}
                        </span>
                    </div>
                    <div className="md:col-span-7 p-6 sm:p-8 flex flex-col justify-center">
                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)]">
                            <Icons.ClipboardList className="h-5 w-5" />
                        </div>
                        <h3 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                            {t("resources.featuredTitle")}
                        </h3>
                        <p className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed">
                            {t("resources.featuredBody")}
                        </p>
                        <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-teal-2)] group-hover:gap-2 transition-[gap]">
                            {t("cta.openStepByStep")} <ArrowRight className="h-4 w-4" />
                        </div>
                    </div>
                </Link>

                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {guides.map((g) => {
                        const Icon = Icons[g.icon] || Icons.Clipboard;
                        return (
                            <Link
                                key={g.slug}
                                to={`/resources/${g.slug}`}
                                data-testid={`resource-card-${g.slug}`}
                                className="card-soft group p-6 flex flex-col gap-3"
                            >
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)]">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <h3 className="font-display text-lg font-semibold text-[color:var(--brand-navy)]">{g.title}</h3>
                                <p className="text-sm text-[color:var(--text-muted)] leading-relaxed line-clamp-3">{g.summary}</p>
                                <div className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-teal-2)] group-hover:gap-2 transition-[gap]">
                                    {t("cta.readGuide")} <ArrowRight className="h-4 w-4" />
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>
        </>
    );
}

export function ResourceDetail() {
    const { t } = useTranslation();
    const { localize } = useLocalize();
    const { slug } = useParams();
    const raw = getPrepGuideBySlug(slug);
    if (!raw) return <Navigate to="/resources" replace />;
    const guide = localize(raw);
    const Icon = Icons[guide.icon] || Icons.Clipboard;
    const tone = guide.callout?.tone || "info";

    function handlePrint() {
        window.print();
    }

    return (
        <>
            <SEO title={guide.title} description={guide.summary} />
            <div className="container-page pt-8">
                <Breadcrumbs items={[{ to: "/resources", label: t("resources.crumb") }, { label: guide.title }]} />
            </div>
            <section className="container-page pt-6 pb-4">
                <div className="grid gap-8 lg:grid-cols-12">
                    <article className="lg:col-span-8">
                        <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)]">
                            <Icon className="h-5.5 w-5.5" />
                        </div>
                        <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                            {guide.title}
                        </h1>
                        <p className="mt-4 text-lg text-[color:var(--text-muted)] leading-relaxed max-w-2xl">
                            {guide.summary}
                        </p>

                        <div className="mt-8 space-y-8">
                            {(guide.sections || []).map((s) => (
                                <div key={s.heading}>
                                    <h2 className="font-display text-2xl font-semibold text-[color:var(--brand-navy)]">{s.heading}</h2>
                                    <ul className="mt-3 space-y-2 pl-5 list-disc marker:text-[color:var(--brand-teal)] text-[color:var(--text-muted)] leading-relaxed">
                                        {s.items.map((i) => <li key={i}>{i}</li>)}
                                    </ul>
                                </div>
                            ))}
                        </div>

                        {guide.callout && (
                            <div
                                className="mt-8 rounded-2xl border p-5 flex gap-3"
                                style={{
                                    borderColor: tone === "warning" ? "rgba(180,83,9,0.35)" : "rgba(29,78,216,0.25)",
                                    background: tone === "warning" ? "rgba(180,83,9,0.06)" : "rgba(29,78,216,0.05)",
                                }}
                            >
                                {tone === "warning" ? (
                                    <AlertTriangle className="h-5 w-5" style={{ color: "var(--warning)" }} />
                                ) : (
                                    <Info className="h-5 w-5" style={{ color: "var(--info)" }} />
                                )}
                                <p className="text-sm text-[color:var(--text-primary)] leading-relaxed">{guide.callout.text}</p>
                            </div>
                        )}
                    </article>
                    <aside className="lg:col-span-4">
                        <div className="card-soft p-6 sticky top-24">
                            <p className="text-xs uppercase tracking-wider text-[color:var(--text-soft)]">{t("resources.takeThisWithYou")}</p>
                            <p className="mt-2 text-sm text-[color:var(--text-muted)]">
                                {t("resources.printSubtitle")}
                            </p>
                            <button
                                onClick={handlePrint}
                                data-testid="resource-print-button"
                                className="mt-4 w-full inline-flex items-center justify-center rounded-xl bg-[color:var(--brand-navy)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[color:var(--brand-navy-2)]"
                            >
                                {t("cta.printSavePdf")}
                            </button>
                            <div className="mt-4 text-xs text-[color:var(--text-soft)] leading-relaxed">
                                {t("resources.questionsCall")}{" "}
                                <a href={`tel:${CLINIC_PHONE.replace(/[^\d+]/g, "")}`} className="text-[color:var(--brand-teal-2)] font-semibold">
                                    {CLINIC_PHONE}
                                </a>
                                .
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </>
    );
}
